import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import { Car, Bike, Trophy, ChevronDown, ChevronUp, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoundResults() {
  const [user, setUser] = useState(null);
  const [activeLeague, setActiveLeague] = useState('racing');
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRound, setExpandedRound] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userRes, snapshotsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/round-results?league=${activeLeague}`, { withCredentials: true }),
        ]);
        setUser(userRes.data);
        setSnapshots(snapshotsRes.data);
      } catch (err) {
        console.error('Failed to load round results:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeLeague]);

  const accentColor = activeLeague === 'racing' ? 'primary' : 'secondary';

  // Build all unique players across all rounds
  const allPlayers = {};
  snapshots.forEach(snap => {
    snap.player_scores.forEach(ps => {
      if (!allPlayers[ps.user_id]) {
        allPlayers[ps.user_id] = { name: ps.name, team_name: ps.team_name };
      }
    });
  });

  // Build score matrix: { user_id -> { roundN: points, total: cumulative } }
  const scoreMatrix = {};
  Object.keys(allPlayers).forEach(uid => { scoreMatrix[uid] = {}; });
  snapshots.forEach(snap => {
    snap.player_scores.forEach(ps => {
      scoreMatrix[ps.user_id][`r${snap.round_number}`] = ps.round_points;
      scoreMatrix[ps.user_id].cumulative = ps.cumulative_points;
    });
  });

  // Sort players by latest cumulative points
  const sortedPlayers = Object.entries(allPlayers)
    .map(([uid, info]) => ({ uid, ...info, cumulative: scoreMatrix[uid]?.cumulative || 0 }))
    .sort((a, b) => b.cumulative - a.cumulative);

  return (
    <PageBackground pageKey="round_results">
    <div className="min-h-screen">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="font-heading font-black text-5xl mb-2" data-testid="round-results-title">
          ROUND <span className={`text-${accentColor}`}>RESULTS</span>
        </h1>
        <p className="text-zinc-400 mb-8">Player scores per round</p>

        {/* League Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveLeague('racing')}
            data-testid="round-results-racing-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'racing' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Car size={20} />
            <MSFLogo suffix="Racing" size="sm" />
          </button>
          <button
            onClick={() => setActiveLeague('moto')}
            data-testid="round-results-moto-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'moto' ? 'bg-secondary text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bike size={20} />
            <MSFLogo suffix="Moto" size="sm" />
          </button>
        </div>

        {loading ? (
          <div className="bg-card border border-zinc-800 p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-zinc-400">Loading round results...</p>
          </div>
        ) : snapshots.length === 0 ? (
          <div className="bg-card border border-zinc-800 p-12 text-center" data-testid="round-results-empty">
            <Trophy className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-lg font-heading font-bold">No round results yet</p>
            <p className="text-zinc-600 text-sm mt-1">Results will appear here after each round is completed</p>
          </div>
        ) : (
          <>
            {/* Championship Table */}
            <div className="bg-card border border-zinc-800 overflow-hidden mb-8" data-testid="round-results-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-900 border-b border-zinc-800">
                      <th className="px-4 py-3 text-left font-heading font-bold text-zinc-400 sticky left-0 bg-zinc-900 z-10 min-w-[50px]">#</th>
                      <th className="px-4 py-3 text-left font-heading font-bold text-zinc-400 sticky left-[50px] bg-zinc-900 z-10 min-w-[180px]">Player</th>
                      {snapshots.map(snap => (
                        <th key={snap.round_number} className="px-4 py-3 text-center font-heading font-bold text-zinc-400 min-w-[80px]">
                          <button
                            onClick={() => setExpandedRound(expandedRound === snap.round_number ? null : snap.round_number)}
                            className={`hover:text-${accentColor} transition-colors`}
                          >
                            R{snap.round_number}
                          </button>
                        </th>
                      ))}
                      <th className={`px-4 py-3 text-center font-heading font-bold text-${accentColor} min-w-[90px]`}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayers.map((player, idx) => {
                      const isCurrentUser = user && player.uid === user.user_id;
                      return (
                        <tr
                          key={player.uid}
                          data-testid={`round-row-${player.uid}`}
                          className={`border-b border-zinc-800/50 transition-colors ${isCurrentUser ? `bg-${accentColor}/5` : idx % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/30'}`}
                        >
                          <td className="px-4 py-3 font-heading font-bold text-zinc-500 sticky left-0 bg-inherit z-10">{idx + 1}</td>
                          <td className="px-4 py-3 sticky left-[50px] bg-inherit z-10">
                            <div>
                              <span className="font-bold text-white">
                                {player.name}
                                {isCurrentUser && <span className={`ml-1 text-xs text-${accentColor}`}>(YOU)</span>}
                              </span>
                              {player.team_name && (
                                <p className={`text-xs text-${accentColor} font-heading`}>{player.team_name}</p>
                              )}
                            </div>
                          </td>
                          {snapshots.map(snap => {
                            const pts = scoreMatrix[player.uid]?.[`r${snap.round_number}`];
                            return (
                              <td key={snap.round_number} className="px-4 py-3 text-center font-mono">
                                {pts !== undefined ? (
                                  <span className={pts > 0 ? 'text-green-400' : pts < 0 ? 'text-red-400' : 'text-zinc-500'}>
                                    {pts > 0 ? `+${pts}` : pts}
                                  </span>
                                ) : (
                                  <span className="text-zinc-700">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className={`px-4 py-3 text-center font-heading font-bold text-${accentColor}`}>
                            {player.cumulative}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Round details */}
            <div className="space-y-3">
              <h2 className="font-heading font-bold text-lg text-zinc-400 mb-2">Round Details</h2>
              {[...snapshots].reverse().map(snap => (
                <div key={snap.round_number} className="bg-card border border-zinc-800">
                  <button
                    onClick={() => setExpandedRound(expandedRound === snap.round_number ? null : snap.round_number)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-zinc-900/50 transition-colors"
                    data-testid={`round-detail-${snap.round_number}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`font-heading font-bold text-xl text-${accentColor}`}>R{snap.round_number}</span>
                      <span className="font-bold text-white">{snap.round_name}</span>
                      <span className="text-xs text-zinc-500">{snap.captured_at || snap.timestamp ? new Date(snap.captured_at || snap.timestamp).toLocaleDateString() : ''}</span>
                    </div>
                    {expandedRound === snap.round_number ? <ChevronUp size={18} className="text-zinc-500" /> : <ChevronDown size={18} className="text-zinc-500" />}
                  </button>
                  {expandedRound === snap.round_number && (
                    <div className="border-t border-zinc-800 p-4">
                      <div className="space-y-1">
                        {snap.player_scores.map((ps, i) => (
                          <div key={ps.user_id} className="flex items-center justify-between px-3 py-2 bg-zinc-900/50 border border-zinc-800/50 text-sm">
                            <div className="flex items-center gap-3">
                              <span className="text-zinc-600 w-6 font-mono">{i + 1}</span>
                              <div>
                                <span className="font-bold text-white">{ps.name}</span>
                                {ps.team_name && <span className={`ml-2 text-xs text-${accentColor}`}>{ps.team_name}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {ps.boost_points > 0 && (
                                <span className="flex items-center gap-1 text-xs text-yellow-400">
                                  <Zap size={12} /> +{ps.boost_points}
                                </span>
                              )}
                              <span className={`font-mono font-bold ${ps.round_points > 0 ? 'text-green-400' : ps.round_points < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                                {ps.round_points > 0 ? `+${ps.round_points}` : ps.round_points}
                              </span>
                              <span className={`font-mono text-${accentColor} font-bold`}>{ps.cumulative_points} pts</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </PageBackground>
  );
}
